import { giphyDarkGrey, giphyLightestGrey } from '@giphy/colors'
import { UGEEditor } from 'creation/src/components/uge-gl/types'
import { useContext } from 'react'
import styled from 'styled-components'
import { IconComponent } from 'ui'
import { BORDER_RADIUS_MEDIUM, DUPLICATE_UPLOAD_ID_EXTENSION } from '../../../constants'
import CreationToolGradient from '../../../types/gradient'
import CreationToolOutputType from '../../../types/output-type'
import CreationToolStatus from '../../../types/status'
import getStickerFromSource from '../../../utils/get-sticker-from-source'
import { MenuAddIcon } from '../../icons'
import { useAvailableEditors } from '../hooks/use-available-editors'
import { useDuplicatableTypes } from '../hooks/use-duplicatable-types'
import { CreationToolUploadContext } from '../manager'
import { CreationToolUploadSource } from '../types'
import Item from './dropdown-item'

const ICON_SIZE = 16

const Dropdown = styled.div`
    background: #000;
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
    color: ${giphyLightestGrey};
    font-size: 15px;
    left: 50%;
    margin-left: -110px;
    padding: 20px;
    position: absolute;
    top: 100%;
    width: 220px;

    &:before {
        border-bottom: 6px solid #000;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        content: '';
        height: 0;
        left: 50%;
        margin-left: -6px;
        position: absolute;
        top: -6px;
        width: 0;
    }
`

const Items = styled.div`
    svg,
    i {
        transition: opacity 0.15s ease-out;
    }

    &:hover svg,
    &:hover i {
        opacity: 0.5;
    }

    &:hover > div:hover svg,
    &:hover > div:hover i {
        opacity: 1;
    }
`

const Divider = styled.div`
    background: ${giphyDarkGrey};
    border-radius: 1px;
    height: 2px;
    margin: 6px 0;
`

type Props = {
    className?: string
    source: CreationToolUploadSource
    setIsPreviewing?: (isPreviewing: boolean) => void
    onEditorClick: (editor?: UGEEditor) => void
}

const CreationUploadItemEditMenuDropdown = ({
    className,
    source,
    setIsPreviewing,
    onEditorClick: editorClickCallback,
}: Props) => {
    const { uploadSources, addUploadSource, updateUploadSource } = useContext(CreationToolUploadContext)
    const availableEditors = useAvailableEditors(source)
    const duplicatableTypes = useDuplicatableTypes(source, uploadSources)
    const showDivider = availableEditors.length > 0 && (source.edited || duplicatableTypes.length > 0)

    const onEditorClick = (editor?: UGEEditor) => {
        editorClickCallback(editor)
    }

    const onDuplicateClick = async (type: CreationToolOutputType) => {
        if (!type) return

        const isConvertToSticker = type === CreationToolOutputType.Sticker
        const newSource = {
            ...source,
            id: `${source.id}${DUPLICATE_UPLOAD_ID_EXTENSION}`,
            status: isConvertToSticker ? CreationToolStatus.ConvertingToSticker : CreationToolStatus.Idle,
            outputType: type,
            isDuplicate: true,
        }

        setIsPreviewing && setIsPreviewing(false)
        addUploadSource(newSource)

        if (isConvertToSticker) {
            const sticker = await getStickerFromSource(newSource.edited || newSource.source)
            updateUploadSource({
                ...newSource,
                edited: undefined,
                source: sticker,
                status: CreationToolStatus.Idle,
            })
        }
    }

    const onRevert = () => {
        updateUploadSource({
            ...source,
            edited: undefined,
        })
    }

    return (
        <Dropdown className={className}>
            <Items>
                <>
                    {availableEditors.map(({ editor, gradient, icon, iconScale = 1, title }) => {
                        const iconHeight = Math.round(16 * iconScale)
                        const iconWidth = Math.round(16 * iconScale)

                        return (
                            <Item
                                key={`upload-item-dropdown-${editor}`}
                                label={title}
                                onClick={() => onEditorClick(editor)}
                            >
                                <IconComponent
                                    color={!gradient ? '#fff' : undefined}
                                    gradient={gradient}
                                    icon={icon}
                                    height={iconHeight}
                                    width={iconWidth}
                                />
                            </Item>
                        )
                    })}
                </>
                {showDivider && <Divider />}
                {duplicatableTypes.length > 0 && (
                    <>
                        {duplicatableTypes.map((type) => {
                            const gradient = CreationToolOutputType.gradient(type)
                            const colors = CreationToolGradient.colors(gradient)

                            return (
                                <Item
                                    key={type}
                                    label={`Duplicate as ${CreationToolOutputType.title(type)}`}
                                    onClick={() => onDuplicateClick(type)}
                                >
                                    <MenuAddIcon colors={colors} />
                                </Item>
                            )
                        })}
                    </>
                )}
                {source.edited && (
                    <Item
                        key={`upload-item-dropdown-${source.edited.id}`}
                        label="Revert to Original"
                        onClick={() => onRevert()}
                    >
                        <IconComponent gradient="red-pink" icon="rotate" height={ICON_SIZE} width={ICON_SIZE} />
                    </Item>
                )}
            </Items>
        </Dropdown>
    )
}

export default CreationUploadItemEditMenuDropdown
